import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote as QuoteIcon } from "lucide-react";
import ArticleCard from "../components/ArticleCard";
import SmartPopup from "../components/SmartPopup";
import NewsTicker from "../components/NewsTicker";
import FeaturedSlider from "../components/FeaturedSlider";
import SafeImage from "../components/SafeImage";
import { useI18n } from "../context/I18nContext";
import api from "../lib/api";

import { LOGO_URL } from "../components/Logo";
const FOUNDER = "https://customer-assets.emergentagent.com/job_gai-editorial/artifacts/4fy44nua_WhatsApp%20Image%202026-04-26%20at%2021.58.08.jpeg";
const COVER = "https://static.prod-images.emergentagent.com/jobs/07281dc8-50f2-40bd-8411-3c785246f765/images/12fdfb85e2a28f0bfaa8e0ab8aae49736e45dee624e268a855a384f983161759.png";
const RDC_IMG = "https://customer-assets.emergentagent.com/job_gai-editorial/artifacts/q39pteqw_WhatsApp%20Image%202026-04-26%20at%2022.02.01.jpeg";

export default function Home() {
  const { t, tr, lang } = useI18n();
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [topMag, setTopMag] = useState(null);
  const [special, setSpecial] = useState(null);

  useEffect(() => {
    api.get("/articles?limit=8").then((r) => {
      const items = r.data || [];
      const f = items.find((a) => a.featured) || items[0];
      setFeatured(f);
      setArticles(items.filter((a) => !f || a.id !== f.id).slice(0, 6));
    }).catch(() => {});
    api.get("/magazines?featured=true&limit=1").then((r) => {
      if (r.data?.[0]) setTopMag(r.data[0]);
    }).catch(() => {});
    api.get("/special-editions").then((r) => {
      setSpecial((r.data || []).find((e) => e.featured) || null);
    }).catch(() => {});
  }, []);

  const editorialFR = `Notre vision est de construire un environnement sain, de pensée et d'écriture, autour des préoccupations majeures des peuples du continent africain.`;
  const editorialEN = `Our vision is to build a healthy environment of thought and writing, around the major concerns of the African continent.`;

  return (
    <div data-testid="page-home" style={{ background: "var(--gai-white)" }}>
      <NewsTicker />

      {/* ============ HERO — Editorial magazine mast ============ */}
      <section className="relative" data-testid="hero-section">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-14 pb-24 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7 gai-fade-up">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-[2px] w-14" style={{ background: "var(--gai-red)" }} />
              <span className="text-[0.66rem] font-bold tracking-[0.28em] uppercase" style={{ color: "var(--gai-red)" }}>
                {lang === "fr" ? "Magazine média panafricain · Édition digitale" : "Pan-African Media Magazine · Digital Edition"}
              </span>
            </div>

            <h1
              className="serif gai-hero-title font-bold notranslate"
              translate="no"
              style={{
                color: "var(--gai-black)",
                fontSize: "clamp(2.4rem, 6vw, 5.2rem)",
                lineHeight: 0.95,
                letterSpacing: "-0.025em",
                fontFamily: "'Playfair Display', serif",
              }}
            >
              <span className="block">GLOBAL</span>
              <span className="block" style={{ color: "var(--gai-red)" }}>AFRIQUE</span>
              <span className="block" style={{ fontSize: "0.55em", letterSpacing: "0.05em", color: "var(--gai-grey-700)" }}>
                International
              </span>
            </h1>

            <div className="mt-8 space-y-1.5 max-w-xl">
              {(lang === "fr"
                ? ["L'Afrique qui pense.", "L'Afrique qui décide.", "L'Afrique qui s'écrit."]
                : ["The Africa that thinks.", "The Africa that decides.", "The Africa that writes itself."]
              ).map((line, i) => (
                <div
                  key={i}
                  className="serif italic gai-fade-up"
                  style={{
                    animationDelay: `${0.25 + i * 0.15}s`,
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "clamp(1.35rem, 2vw, 1.85rem)",
                    color: i === 1 ? "var(--gai-red)" : "var(--gai-charcoal)",
                    fontWeight: 500,
                  }}
                >
                  {line}
                </div>
              ))}
            </div>

            <p className="mt-8 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "var(--gai-grey-700)" }}>
              {lang === "fr"
                ? "Finance, politique, culture, sport, institutions : un récit africain rigoureux, à hauteur d'enjeux mondiaux."
                : "Finance, politics, culture, sport, institutions: a rigorous African narrative at world-stake level."}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/magazine" className="gai-btn-gold" data-testid="hero-cta-magazine">
                {lang === "fr" ? "Feuilleter l'Édition N°0" : "Read Edition N°0"} <ArrowRight size={14} />
              </Link>
              <Link to="/annoncer" className="gai-btn-outline" data-testid="hero-cta-advertise">
                {lang === "fr" ? "Annoncer dans GAI" : "Advertise in GAI"}
              </Link>
            </div>

            <div className="mt-14 flex items-center gap-5 pt-8" style={{ borderTop: "1px solid var(--gai-grey-200)", maxWidth: "36rem" }} data-testid="hero-founder">
              <div className="relative shrink-0">
                <SafeImage
                  src={FOUNDER}
                  alt="Bijoux Milton Kilandamoko"
                  className="w-20 h-24 object-cover"
                  fallbackLabel="Fondatrice"
                />
              </div>
              <div>
                <div className="text-[0.62rem] font-bold tracking-[0.24em] uppercase mb-1" style={{ color: "var(--gai-red)" }}>La Fondatrice</div>
                <div className="serif text-xl" style={{ color: "var(--gai-black)" }} translate="no">Bijoux Milton Kilandamoko</div>
                <div className="text-[0.7rem] tracking-[0.18em] uppercase mt-1" style={{ color: "var(--gai-grey-500)" }}>
                  Fondatrice & Éditrice
                </div>
                <Link to="/fondatrice" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--gai-red)" }} data-testid="hero-founder-link">
                  Découvrir son parcours <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 gai-fade-up gai-delay-2 max-w-md mx-auto lg:max-w-none lg:mx-0 w-full">
            <Link to={topMag ? `/magazine/${topMag.id}` : "/magazine"} className="block group" data-testid="hero-magazine-cover">
              <div className="relative aspect-[3/4]">
                <div
                  className="absolute -inset-3 transition-all duration-500 group-hover:-inset-4"
                  style={{ border: "1px solid var(--gai-grey-300)" }}
                  aria-hidden
                />
                {/* Real cover extracted from page 1 of the Édition N°0 PDF */}
                <div
                  className="relative w-full h-full overflow-hidden bg-[var(--gai-grey-100)]"
                  style={{ boxShadow: "0 30px 60px -20px rgba(0,0,0,0.20)" }}
                >
                  <SafeImage
                    src={topMag?.cover_url}
                    alt={topMag ? tr(topMag.title) : "Édition N°0"}
                    fallbackLabel="ÉDITION N°0"
                    loading="eager"
                    className="w-full h-full object-cover"
                    style={{ width: "100%", height: "100%" }}
                    testid="hero-cover-image"
                  />
                  <div
                    className="absolute top-0 left-0 h-1 w-1/3"
                    style={{ background: "var(--gai-red)" }}
                    aria-hidden
                  />
                  <div className="absolute top-4 left-4 px-3 py-1.5 font-bold text-[0.6rem] tracking-[0.22em] uppercase"
                    style={{ background: "var(--gai-red)", color: "var(--gai-white)" }}>
                    {lang === "fr" ? "Édition N°0" : "Edition N°0"}
                  </div>
                </div>
              </div>
              <div className="mt-5 flex items-start justify-between gap-3">
                <div>
                  <div className="text-[0.66rem] font-bold tracking-[0.22em] uppercase" style={{ color: "var(--gai-red)" }}>
                    {topMag?.edition || "N°0 — Première édition historique"}
                  </div>
                  <div className="serif text-xl mt-1" style={{ color: "var(--gai-black)" }} translate="no">
                    {topMag ? tr(topMag.title) : "GLOBAL AFRIQUE INTERNATIONAL — Édition N°0"}
                  </div>
                </div>
                <span className="text-lg mt-1" style={{ color: "var(--gai-red)" }}>→</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FEATURED SLIDER (light bg) ============ */}
      <FeaturedSlider lang={lang} />

      {/* ============ EDITORIAL VISION QUOTE — light band ============ */}
      <section style={{ background: "var(--gai-white)", color: "var(--gai-black)", borderTop: "1px solid var(--gai-grey-200)", borderBottom: "1px solid var(--gai-grey-200)" }} data-testid="home-editorial">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-24 lg:py-28 text-center relative">
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-1"
            style={{ background: "var(--gai-red)" }}
          />
          <QuoteIcon size={36} style={{ color: "var(--gai-red)" }} className="mx-auto" />
          <p
            className="serif italic mt-8 leading-[1.35]"
            style={{
              fontSize: "clamp(1.6rem, 3vw, 2.5rem)",
              color: "var(--gai-black)",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            « {lang === "fr" ? editorialFR : editorialEN} »
          </p>
          <div className="mt-12 flex items-center justify-center gap-5">
            <img src={FOUNDER} alt="Bijoux Milton Kilandamoko" className="w-14 h-14 object-cover rounded-full" style={{ border: "1px solid var(--gai-grey-200)" }} />
            <div className="text-left">
              <div className="serif text-lg" style={{ color: "var(--gai-black)" }} translate="no">Bijoux Milton Kilandamoko</div>
              <div className="text-[0.66rem] tracking-[0.22em] uppercase font-bold mt-0.5" style={{ color: "var(--gai-red)" }}>
                {lang === "fr" ? "Fondatrice & Éditrice — Édition N°0" : "Founder & Publisher — Edition N°0"}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SPECIAL EDITION PROMO — featured edition from admin ============ */}
      {special && (() => {
        const pk = (bi) => (bi && (bi[lang] || bi.fr || bi.en)) || "";
        return (
          <section className="max-w-[1320px] mx-auto px-6 lg:px-10 mt-24" data-testid="home-special-promo">
            <Link
              to={`/edition-speciale/${special.slug}`}
              className="group block relative overflow-hidden"
              style={{ background: "var(--gai-black)", color: "var(--gai-white)" }}
              data-testid="home-special-cta"
            >
              <div className="absolute top-0 left-0 h-1 w-32 z-10" style={{ background: "var(--gai-red)" }} aria-hidden />
              <div className="grid lg:grid-cols-12 gap-0 items-stretch">
                <div className="lg:col-span-5 relative overflow-hidden" style={{ minHeight: 320 }}>
                  <SafeImage
                    src={special.cover_photo_url}
                    alt={`${pk(special.name_title)} ${special.name_line1} ${special.name_line2} ${special.name_accent}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    style={{ width: "100%", height: "100%", objectPosition: special.photo_focus || "center 20%" }}
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, transparent 30%, rgba(0,0,0,0.55) 100%)" }} aria-hidden />
                </div>
                <div className="lg:col-span-7 p-8 md:p-14 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-3 self-start px-3 py-1.5" style={{ background: "var(--gai-red)" }}>
                    <span className="text-[0.6rem] tracking-[0.3em] uppercase font-bold">
                      {pk(special.kicker)}{pk(special.event_full_title) ? ` · ${pk(special.event_full_title)}` : ""}
                    </span>
                  </div>
                  {pk(special.name_title) && (
                    <div className="mt-5 text-[0.72rem] md:text-[0.8rem] tracking-[0.2em] uppercase font-bold" style={{ color: "rgba(255,255,255,0.85)" }} data-testid="home-special-name-title">
                      {pk(special.name_title)}
                    </div>
                  )}
                  <h2
                    className="serif font-bold mt-2 leading-[0.98]"
                    style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.9rem, 4vw, 3.4rem)", color: "var(--gai-white)" }}
                    translate="no"
                    data-testid="home-special-name"
                  >
                    {special.name_line1} {special.name_line2} <span style={{ color: "var(--gai-red)" }}>{special.name_accent}</span>
                  </h2>
                  <div className="serif italic mt-3 text-xl md:text-2xl" style={{ color: "rgba(255,255,255,0.78)", fontFamily: "'Playfair Display', serif" }}>
                    {pk(special.subtitle_italic)}
                  </div>
                  <p className="mt-5 max-w-xl text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.72)" }}>
                    {pk(special.promo_description)}
                  </p>
                  <div className="mt-7 flex items-center gap-4 text-[0.68rem] tracking-[0.24em] uppercase font-bold">
                    <span style={{ color: "var(--gai-red)" }}>{lang === "fr" ? "Lire l'édition spéciale" : "Read the special edition"}</span>
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </Link>
          </section>
        );
      })()}

      {/* ============ LATEST ARTICLES ============ */}
      <section className="max-w-[1320px] mx-auto px-6 lg:px-10 mt-24" data-testid="home-latest">
        <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
          <div>
            <div className="text-[0.66rem] font-bold tracking-[0.24em] uppercase mb-3" style={{ color: "var(--gai-red)" }}>
              {t("home.latest")}
            </div>
            <h2 className="serif text-4xl md:text-5xl" style={{ color: "var(--gai-black)", fontFamily: "'Playfair Display', serif" }}>
              {t("articles.title")}
            </h2>
          </div>
          <Link to="/articles" className="gai-btn-ghost" data-testid="home-view-all">
            {t("home.viewAll")} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="h-[2px] w-24 mb-10" style={{ background: "var(--gai-red)" }} />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured && <ArticleCard article={featured} featured testid="home-featured-article" />}
          {articles.map((a) => <ArticleCard key={a.id} article={a} testid={`home-article-${a.id}`} />)}
        </div>
      </section>

      {/* ============ RDC FOCUS ============ */}
      <section className="mt-28" data-testid="home-rdc" style={{ background: "var(--gai-grey-50)" }}>
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <SafeImage src={topMag?.cover_url || RDC_IMG} alt="Édition N°0" fallbackLabel="ÉDITION N°0" className="w-full object-cover" />
          </div>
          <div className="lg:col-span-6">
            <div className="text-[0.66rem] font-bold tracking-[0.24em] uppercase mb-4" style={{ color: "var(--gai-red)" }}>
              {lang === "fr" ? "Édition N°0 · Première édition historique" : "Edition N°0 · First historical edition"}
            </div>
            <h2 className="serif text-4xl md:text-5xl leading-tight" style={{ color: "var(--gai-black)", fontFamily: "'Playfair Display', serif" }}>
              {lang === "fr"
                ? "Les origines du Magazine d'Affaires Panafricain."
                : "The origins of the Pan-African Business Magazine."}
            </h2>
            <div className="h-[2px] w-24 my-6" style={{ background: "var(--gai-red)" }} />
            <p className="leading-relaxed text-lg" style={{ color: "var(--gai-grey-700)" }}>
              {lang === "fr"
                ? "Découvrez l'édition fondatrice de Global Afrique International : sa ligne éditoriale, son positionnement panafricain, son lectorat, son approche des affaires et de la communication ainsi que les premières publications qui ont construit l'identité du magazine."
                : "Discover the founding edition of Global Afrique International: its editorial line, its pan-African positioning, its readership, its approach to business and communication, and the first publications that shaped the magazine's identity."}
            </p>
            <Link to={topMag ? `/magazine/${topMag.id}` : "/magazine"} className="mt-8 gai-btn-gold inline-flex" data-testid="home-rdc-cta">
              {lang === "fr" ? "Feuilleter l'Édition N°0" : "Read Edition N°0"} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ REACH / KPIs — Light band ============ */}
      <section data-testid="home-reach" style={{ background: "var(--gai-white)", color: "var(--gai-black)", borderTop: "1px solid var(--gai-grey-200)" }}>
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-[0.66rem] font-bold tracking-[0.24em] uppercase mb-4" style={{ color: "var(--gai-red)" }}>
            {t("home.reachOverline")}
          </div>
          <h2 className="serif text-3xl md:text-4xl max-w-3xl" style={{ color: "var(--gai-black)", fontFamily: "'Playfair Display', serif" }}>
            {t("home.reachTitle")}
          </h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-0" style={{ border: "1px solid var(--gai-grey-200)" }}>
            {t("home.reachItems").map((it, i) => (
              <div
                key={i}
                className="p-8"
                style={{
                  borderRight: i < 3 ? "1px solid var(--gai-grey-200)" : "none",
                  borderBottom: "none",
                }}
                data-testid={`kpi-${i}`}
              >
                <div className="serif text-5xl md:text-6xl leading-none font-bold" style={{ color: "var(--gai-red)", fontFamily: "'Playfair Display', serif" }}>
                  {it.k}
                </div>
                <div className="mt-3 text-[0.66rem] tracking-[0.22em] uppercase font-semibold" style={{ color: "var(--gai-grey-700)" }}>
                  {it.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 3 CTA BLOCKS — Read / Kiosk / Advertise ============ */}
      <section className="mt-24 mb-16 px-6 lg:px-10" data-testid="home-quote-cta">
        <div className="max-w-[1320px] mx-auto grid md:grid-cols-3 gap-6">
          {[
            {
              overline: lang === "fr" ? "Lire" : "Read",
              title: lang === "fr" ? "Articles à l'unité ou magazine complet — à partir de 4 €" : "Articles per piece or full magazine — from €4",
              cta: lang === "fr" ? "Voir les articles" : "Browse articles",
              to: "/articles",
              testid: "home-cta-articles",
              accent: false,
            },
            {
              overline: lang === "fr" ? "Feuilleter" : "Read",
              title: lang === "fr" ? "Le kiosque numérique — édition complète feuilletable" : "Digital kiosk — full edition flippable",
              cta: lang === "fr" ? "Le kiosque" : "Open kiosk",
              to: "/magazine",
              testid: "home-cta-kiosk",
              accent: false,
            },
            {
              overline: lang === "fr" ? "Annoncer" : "Advertise",
              title: lang === "fr" ? "Mettez votre marque sous les yeux des décideurs africains" : "Put your brand before African decision-makers",
              cta: lang === "fr" ? "Devenir annonceur" : "Become an advertiser",
              to: "/annoncer",
              testid: "home-cta-advertise",
              accent: true,
            },
          ].map((c, i) => (
            <Link
              key={i}
              to={c.to}
              className="group block p-10 transition-all"
              data-testid={c.testid}
              style={{
                background: c.accent ? "var(--gai-black)" : "var(--gai-grey-50)",
                color: c.accent ? "var(--gai-white)" : "var(--gai-black)",
                border: `1px solid ${c.accent ? "var(--gai-black)" : "var(--gai-grey-200)"}`,
              }}
            >
              <div className="text-[0.66rem] font-bold tracking-[0.24em] uppercase mb-4" style={{ color: "var(--gai-red)" }}>
                {c.overline}
              </div>
              <h3 className="serif text-2xl leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {c.title}
              </h3>
              <div className="mt-6 inline-flex items-center gap-2 text-[0.72rem] font-bold tracking-[0.18em] uppercase" style={{ color: c.accent ? "var(--gai-red)" : "var(--gai-black)" }}>
                {c.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <SmartPopup />
    </div>
  );
}
